import * as React from "react";
import * as rt from "../../../../lib/SimulationReportModels";
import * as t from "../../../../lib/backend/reports.generated.types";
import * as BusinessLogic from "../../../../lib/BusinessLogic";
import * as t2 from "../../../../lib/backend/manufacturing2.generated.types";
import Order from "./Order";
import { TIME_RANGES, dateFromShiftTime, shiftTag, shiftNumber } from "../../../../lib/Util";
import { orderColor } from "../../../../lib/Insights";
import TranslatedMaterialTitle from "../../../TranslatedMaterialTitle";
import { ORDER_TAGS_ACCESS, PLANT_TAGS_ACCESS } from "../../../../lib/ManufacturingTags.generated";
import { ORDER_STATUS, OrderTypeEnum } from "../../../../lib/ManufacturingConsts.generated";
import { PropertiesLogic } from "../reducers/BusinessLogic";
import ReworkOrderLinks from "./ReworkOrderLinks";
import * as srm from "../../../../lib/SimulationReportModels";

import type { ReworkParams } from "./ReworkOrderLinks";
import type { LineOrders, IOrderProducedModelEx } from "../reducers/linesOrders";
import type { PropertiesState, GanttChartSourceTypes } from "../reducers/properties";
import type { InsightsMapped } from "../reducers/insights";
import type { OwnProps as OrderRectangleProps } from "./OrderRectangle";
import type { MaterialTitleProps } from "./MaterialTitle";
import type { ValidOrder } from "../reducers/common";
import type { AvailableInsightTypes } from "../../../../lib/Insights";
import type { ITags } from "../../../../lib/Models";

const invalidProperties = (properties: PropertiesState): boolean => {
    const { current_shift, week, year, xscale } = properties;

    if (current_shift === null) {
        console.error("Line.jsx, invalidProperties: Current shift is missing");
        return true;
    }

    if (week === null) {
        console.error("Line.jsx, invalidProperties: week is missing");
        return true;
    }

    if (year === null) {
        console.error("Line.jsx, invalidProperties: year is missing");
        return true;
    }

    if (xscale === null) {
        console.error("Line.jsx, invalidProperties: xscale is missing");
        return true;
    }

    return false;
};

type Props = {
    line: rt.ISimulationReportOrderLineEx;
    shifts: t.ISimulationReportOrderLineShiftFeature;
    properties: PropertiesState;
    original_report_created_at: number | null;
    handleOrderClick: (e: Event) => void;
    line_index: number;
    line_uuid: string;
    insights_mapped: InsightsMapped;
    insights: t2.IEventDataEx[];
    line_offset: number;
    valid_orders_map: Map<string, ValidOrder> | null;
    is_parallel_line: boolean;
    source_type: GanttChartSourceTypes;
    is_extra_line: boolean;
    filtered_insight_types: AvailableInsightTypes[];
    lines_with_orders?: LineOrders;
    line_external_id?: string;
    report_data: srm.IReportModelEx | null;
    orders: t2.IOrderModelBase[];
    waterfall_mode?: boolean;
};

class Line extends React.PureComponent<Props> {
    renderOrders = () => {
        // components
        let Orders: React.ReactNode[] = [];

        const properties: PropertiesState = this.props.properties;
        const source_type = properties.source_type;

        const orders = this.props.line.production;
        const bars_start = this.props.properties.bars_start;

        const { show_only_tool_changes }: PropertiesState = properties;
        const report_data = this.props.report_data;
        if (invalidProperties(properties) || !report_data) {
            return null;
        }
        const from_shift_time_ts = dateFromShiftTime(report_data.result.from_shift_time).getTime();
        const next_shift_time_ts = dateFromShiftTime(report_data.result.next_shift_time).getTime();
        const next_shift_time_h = (next_shift_time_ts - from_shift_time_ts) / TIME_RANGES.HOUR;
        const shift_tag_index = new Map<string, number>();
        const shifts = this.props.shifts.shifts;

        for (let i = 0; i < shifts.length; i++) {
            const shift_time = shifts[i].shift_time;

            const shift_tag = shiftTag(shift_time.year, shift_time.week, shift_time.shift_number);
            shift_tag_index.set(shift_tag, i);
        }
        // prepare map of all orders from valid line if we have it
        const valid_orders_map: Map<string, ValidOrder> = this.props.valid_orders_map || new Map();
        const all_orders_map: Map<string, ReworkParams> = new Map();
        const rework_orders: IOrderProducedModelEx[] = [];

        const base_orders_map: Map<string, t2.IOrderModelBase> = new Map(
            this.props.orders.map(order => [order.uuid, order])
        );

        const plant_tags = BusinessLogic.getPlantTags(this.props.line.plant);
        const display_system_status_released_false =
            PLANT_TAGS_ACCESS.planning_table_display_system_status_released(plant_tags);

        for (let i = 0; i < orders.length; i++) {
            const xscale = properties.xscale;
            if (!xscale) {
                console.error("Xscale not set");
                return null;
            }

            const order = orders[i];

            const estimated_start = order.estimated_start[1].val;
            const estimated_completion = order.estimated_completion[1].val;
            const estimated_end_ts = from_shift_time_ts + estimated_completion * TIME_RANGES.HOUR;

            let estimated_tool_setup_end = 0;
            if (order.estimated_tool_setup_end) {
                estimated_tool_setup_end = order.estimated_tool_setup_end[1].val;
            }

            // dont display work orders with estimated_diration == 0
            if (order.estimated_duration[1].val === 0 && estimated_start === estimated_completion) {
                continue;
            }
            if (show_only_tool_changes && estimated_tool_setup_end === estimated_start) {
                continue;
            }

            /* COORDINATES */
            // x0 (x), y0 (y), w (width), x1_2 (middle)

            // start of the order in hours
            let time_start = estimated_start;

            // beginning of the order in pixels
            let x0 = bars_start + xscale(time_start);
            const y0 = this.props.waterfall_mode
                ? this.props.line_offset + i * properties.bar_height
                : this.props.line_offset;

            // length of an order in pixels
            // ending of an order in hours
            let time_end = show_only_tool_changes ? estimated_tool_setup_end : estimated_completion;
            let w = xscale(time_end - time_start);

            // middle of an order in pixels
            // used for central position of the material title
            let x1_2 = x0 + w / 2;
            // counter of full shifts inside of an order

            /* LINE ORDER */

            let line_order: IOrderProducedModelEx | null = null;
            const lines_with_orders = this.props.lines_with_orders;
            let order_sim_index = 0;
            if (lines_with_orders) {
                order_sim_index = lines_with_orders.orders.findIndex(o => o.uuid === order.order_id);
                if (order_sim_index >= 0) {
                    line_order = lines_with_orders.orders[order_sim_index];
                }
            }

            /* TAGS */

            let order_tags: ITags = {};
            if (line_order) {
                order_tags = line_order.tags;
            } else {
                const base_order = base_orders_map.get(order.order_id);
                if (base_order !== undefined) {
                    order_tags = base_order.tags;
                }
            }

            /* COLOR */

            const has_insight = this.props.insights_mapped.has(order.order_id);
            const insights = this.props.insights_mapped.get(order.order_id);

            const is_unreleased =
                !ORDER_TAGS_ACCESS.system_status_released(order_tags) && display_system_status_released_false;
            const is_cleaning_order = ORDER_TAGS_ACCESS.cleaning_order(order_tags);

            const order_color = orderColor({
                order_type: order.order_type,
                has_insight,
                failed_scheduling: order.failed_scheduling,
                filtered_insight_types: this.props.filtered_insight_types,
                insights,
                is_unreleased,
                is_cleaning_order,
            });

            /* OPACITY */

            let is_translucent = false;
            if (PropertiesLogic.isPlanningTable(this.props.source_type)) {
                const valid_order = valid_orders_map.get(order.order_id);
                is_translucent = Boolean(valid_order && valid_order.is_translucent);
            }

            const order_closed = order.order_status !== ORDER_STATUS.open;
            const is_planned = order.order_type === "plan";
            let fill_opacity;
            if (is_planned) {
                fill_opacity = 0.3;
            } else if (order_closed || is_translucent) {
                fill_opacity = 0.1;
            }

            // iterate all shifts of order and draw a rect for each shift
            // it seems end_shift_number is not used?
            let end_shift_number = 0;

            /* INSIGHTS */

            const insight_tooltips = (this.props.insights_mapped.get(order.order_id) || []).map(insight =>
                BusinessLogic.getInsightTooltipContent(insight)
            );
            const insight_hash = (this.props.insights_mapped.get(order.order_id) || [])
                .map(insight => insight.tags["hash"] || "")
                .join(",");

            /* HISTORICAL */

            let is_historical = false;
            let history_x: number | null = null;
            if (estimated_start < next_shift_time_h && estimated_completion > next_shift_time_h) {
                history_x = bars_start + xscale(next_shift_time_h);
            } else if (estimated_start < next_shift_time_h) {
                history_x = bars_start + xscale(estimated_completion);
                is_historical = true;
            }

            const deadline = ORDER_TAGS_ACCESS.required_deadline(order_tags);
            const exceeds_deadline = deadline > 0 && estimated_end_ts > deadline;
            const priority = ORDER_TAGS_ACCESS.priority(order_tags);

            // Used in ReworkOrderLinks
            all_orders_map.set(order.order_id, {
                x: x0 + w / 2,
                y: y0 + properties.bar_height,
                width: w,
                order_uuid: order.order_id,
            });

            const is_linked_operation = ORDER_TAGS_ACCESS.linked_operation(order_tags) !== "";
            if (line_order && is_linked_operation) {
                rework_orders.push(line_order);
            }

            /* DISABLED SHIFTS */

            const first_shift = shiftNumber(new Date(from_shift_time_ts + estimated_start * TIME_RANGES.HOUR));
            const first_shift_tag = shiftTag(first_shift.year, first_shift.week, first_shift.shift);
            const last_shift = shiftNumber(new Date(estimated_end_ts));
            const last_shift_tag = shiftTag(last_shift.year, last_shift.week, last_shift.shift);
            const from_index = shift_tag_index.get(first_shift_tag);
            if (from_index === undefined) {
                continue;
            }

            let to_index = shift_tag_index.get(last_shift_tag);
            if (to_index === undefined) {
                to_index = shifts.length - 1;
            }

            const disabled_shifts = shifts.slice(from_index, to_index).filter(shift => !shift.enabled);

            const order_rectangle_attributes: Partial<OrderRectangleProps> = {
                x: x0,
                y: y0,
                rx: 0,
                ry: 0,
                width: w,
                height: properties.bar_height,
                fill: order_color,
                order,
                has_insight,
                insight_tooltips,
                insight_hash,
                line_uuid: this.props.line.line,
                order_uuid: order.order_id,
                source_type,
                insight_freeze_order: order.insight_freeze_order,
                earliest_end: estimated_end_ts,
                order_index: i,
                line_index: this.props.line_index,
                is_extra_line: this.props.is_extra_line,
                is_filter_highlighted: false,
                production_version: ORDER_TAGS_ACCESS.production_version(order_tags),
                history_x,
                fill_opacity,
                exceeds_deadline,
                priority,
            };

            const material_title_attributes: Partial<MaterialTitleProps> = {
                x: x1_2,
                y: y0 + properties.bar_height / 2 + 5,
                material_title: TranslatedMaterialTitle({
                    material_title: order.material_title,
                    material_external_id: order.material,
                }),
                width: w,
                order_uuid: order.order_id,
                source_type,
                material_external_id: order.material,
                failed_scheduling: order.failed_scheduling,
                is_translucent,
                order_rect_x: x0,
                order_rect_y: y0,
                order_width: w,
                earliest_end: time_end,
                order_index: i,
                line_index: this.props.line_index,
                line_uuid: this.props.line.line,
                is_extra_line: this.props.is_extra_line,
            };

            Orders.push(
                <Order
                    key={i}
                    order_uuid={order.order_id}
                    order_index={i}
                    order_sim_index={order_sim_index}
                    order_type={order.order_type as OrderTypeEnum}
                    line_uuid={this.props.line.line}
                    line_index={this.props.line_index}
                    order_color={order_color}
                    material_title={order.material_title}
                    order_rectangle_attributes={order_rectangle_attributes as OrderRectangleProps}
                    material_title_attributes={material_title_attributes as MaterialTitleProps}
                    last_shift_number={end_shift_number}
                    is_parallel_line={this.props.is_parallel_line}
                    sim_ignore_earliest_start={order.sim_ignore_earliest_start}
                    order_process_num={order.process_num}
                    material_external_id={order.material}
                    disabled_shifts={disabled_shifts}
                    is_linked_operation={is_linked_operation}
                    is_historical={is_historical}
                />
            );
        }

        return (
            <>
                {Orders}
                <ReworkOrderLinks rework_orders={rework_orders} all_orders_map={all_orders_map} />
            </>
        );
    };

    render() {
        return (
            <g id="gantt-chart-line-group" line-uuid={this.props.line_uuid} line-index={this.props.line_index}>
                {this.renderOrders()}
            </g>
        );
    }
}

export default Line;
